<template>
  <div style="display: none">
    <label for="uploadImgForm"></label>
    <input
      id="uploadImgForm"
      ref="uploadImgForm"
      type="file"
      :accept="supportedExtensions"
      :multiple="multiple"
      @change="onFileChanged($event)"
    />
  </div>
</template>

<script setup lang="ts">
import useFile from "../../../composables/useFile";
import { FileOptions } from "../../../../types/FileOptions";
import { ref, computed } from "vue";

const props = withDefaults(
  defineProps<{
    path: string;
    multiple?: boolean;
    options: FileOptions;
  }>(),
  {
    multiple: false,
  },
);

const emit = defineEmits([
  "fileUploadError",
  "fileUploadStarted",
  "fileUploaded",
  "filesUploaded",
  "notify",
]);

const { supportedExtensions, _uploadFile, fetchListing } = useFile(
  props.options,
  emit,
);

const uploadImgForm = ref();

const multiple = computed(() => {
  if (props.options.preferences?.maxUploadingFiles === undefined)
    return props.multiple;
  if (props.options.preferences?.maxUploadingFiles > 1) return props.multiple;
  return false;
});

const onFileChanged = ($event: Event) => {
  const target = $event.target as HTMLInputElement;
  const files = target.files as FileList;

  uploadFiles(files, props.path);
};

const uploadFiles = async (files: FileList | File[], path = "/") => {
  let limit = props.options.preferences?.maxUploadingFiles;
  if (limit !== undefined && limit === 0) {
    limit = 1;
  }

  // Fetch the directory listing once for all files (deduplicates N parallel GETs).
  // On success (even empty folder), prefetch is IImage[] and skips per-file GETs.
  // On failure, prefetch is null and upload is aborted.
  const prefetch = await fetchListing(path);
  if (prefetch === null) {
    emit("fileUploadError", new Error("Failed to fetch directory listing"));
    if (uploadImgForm && uploadImgForm.value) uploadImgForm.value.value = null;
    return;
  }

  emit("fileUploadStarted");

  const promises = [];
  const uploadedNames = new Set<string>();
  let count = 0;
  for (const file of Array.from(files)) {
    if (limit !== undefined && count >= limit) {
      emit("notify", "limit-exceeded:" + files.length);
      break;
    }
    promises.push(_uploadFile(file, path, undefined, prefetch, uploadedNames));
    count++;
  }

  const res = await Promise.allSettled(promises);
  emit("filesUploaded", res);
  if (uploadImgForm && uploadImgForm.value) uploadImgForm.value.value = null;
};

const uploadFile = async (file: File, path = "/") => {
  emit("fileUploadStarted");
  try {
    const res = await _uploadFile(file, path);

    emit("fileUploaded", res);
    return res;
  } catch (e) {
    emit("fileUploadError", e);
  }
};

defineExpose({
  uploadFiles,
  uploadFile,
});
</script>
